import { Component, Inject, ViewChild, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { BasePreviewComponent } from '../base-preview.component';
import { AudioMiniPlayerService } from '../audio-mini-player/audio-mini-player.service';
import { UNSUPPORTED_FILE_PREVIEW_MESSAGE } from 'src/app/constants/app.constants';
import { UserService } from 'src/app/services/core/user.service';
import { DocumentMarkupService } from 'src/app/modules/document/document-image-annotator/document-markup.data.service';
import { Comment as AudioComment, CommentPriority, CommentStatus } from 'src/app/modules/document/document-image-annotator/media-annotation.types';

let audioCmtSeq = 0;

@Component({
    selector: 'app-audio-preview',
    templateUrl: './audio-preview.component.html',
    styleUrls: ['./audio-preview.component.scss'],
    standalone: false
})
export class AudioPreviewComponent extends BasePreviewComponent implements OnInit, OnDestroy {
  @ViewChild('audioPlayer', { static: false }) audioPlayer!: ElementRef<HTMLAudioElement>;

  // Audio State
  isPlaying = false;
  isLoading = true;
  currentTime = 0;
  duration = 0;
  buffered = 0;
  volume = 1;
  isMuted = false;
  playbackRate = 1;

  // UI State
  isDraggingTimeline = false;
  playbackRateOptions = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
  waveformBars = Array(40).fill(0).map(() => Math.random() * 50 + 50);

  // Fullscreen (dialog resize, PDF-style)
  isFullscreen = false;

  // Comments (timestamp-anchored, same sidebar + persistence as the CSV viewer)
  documentId = '';
  isPreDefined = false;
  comments: AudioComment[] = [];
  showCommentsSidebar = false;
  activeCommentId: string | null = null;
  newCommentText = '';
  newCommentTime: number | null = null;   // popup open when non-null
  private commentsLoaded = false;
  private commentsDirty = false;
  private commentSaveTimer: any = null;

  constructor(
    public override dialogRef: MatDialogRef<AudioPreviewComponent>,
    @Inject(MAT_DIALOG_DATA) public override data: any,
    private mini: AudioMiniPlayerService,
    private userService: UserService,
    private markupService: DocumentMarkupService,
    private toastr: ToastrService
  ) {
    super(dialogRef, data);
    this.documentId = this.model?.id || '';
    this.isPreDefined = this.model?.isPreDefined || false;
  }

  ngOnInit(): void {
    this.loadComments();
    // The header now carries more actions; widen a little so the title doesn't collide.
    this.applyDialogSize();
  }

  ngOnDestroy(): void {
    this.flushComments();
  }

  get currentUserName(): string {
    const n = (this.userService.getFullName() || '').replace(/\b(null|undefined)\b/g, '').replace(/\s+/g, ' ').trim();
    return n || 'You';
  }

  // ========================================
  // Audio Event Handlers
  // ========================================

  onAudioLoaded(): void {
    const audio = this.audioPlayer?.nativeElement;
    if (audio) {
      this.duration = audio.duration;
      this.isLoading = false;
      // Resume position/state when re-opened from the mini-player.
      if (this.data?.startTime) { try { audio.currentTime = this.data.startTime; } catch {} this.data.startTime = 0; }
      if (this.data?.autoplay) { this.data.autoplay = false; audio.play().catch(() => {}); }
    }
  }

  onTimeUpdate(): void {
    const audio = this.audioPlayer?.nativeElement;
    if (audio && !this.isDraggingTimeline) {
      this.currentTime = audio.currentTime;
      this.updateBuffered();
    }
  }

  onPlay(): void {
    this.isPlaying = true;
  }

  onPause(): void {
    this.isPlaying = false;
  }

  onWaiting(): void {
    this.isLoading = true;
  }

  onCanPlay(): void {
    this.isLoading = false;
  }

  /**
   * Fired when the browser cannot load/decode the audio (unsupported
   * format/codec, e.g. .wma / .amr). Show the generic unsupported-file toast
   * and close the preview so the user isn't left on a stuck "Loading…" state.
   */
  onAudioError(): void {
    this.isLoading = false;
    this.toastr.error(UNSUPPORTED_FILE_PREVIEW_MESSAGE);
    this.dialogRef.close();
  }

  updateBuffered(): void {
    const audio = this.audioPlayer?.nativeElement;
    if (audio && audio.buffered.length > 0) {
      this.buffered = audio.buffered.end(audio.buffered.length - 1);
    }
  }

  // ========================================
  // Playback Controls
  // ========================================

  togglePlay(): void {
    const audio = this.audioPlayer?.nativeElement;
    if (audio) {
      if (this.isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
    }
  }

  seekTo(time: number): void {
    const audio = this.audioPlayer?.nativeElement;
    if (audio) {
      audio.currentTime = time;
      this.currentTime = time;
    }
  }

  skip(seconds: number): void {
    const audio = this.audioPlayer?.nativeElement;
    if (audio) {
      audio.currentTime = Math.max(0, Math.min(audio.duration, audio.currentTime + seconds));
    }
  }

  onTimelineClick(event: MouseEvent): void {
    const timeline = event.currentTarget as HTMLElement;
    const rect = timeline.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * this.duration;
    this.seekTo(newTime);
  }

  onTimelineDragStart(event: MouseEvent): void {
    this.isDraggingTimeline = true;
    this.onTimelineDrag(event);
  }

  @HostListener('document:mousemove', ['$event'])
  onTimelineDrag(event: MouseEvent): void {
    if (!this.isDraggingTimeline) return;

    const timeline = document.querySelector('.audio-timeline-bar') as HTMLElement;
    if (timeline) {
      const rect = timeline.getBoundingClientRect();
      const clickX = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
      const percentage = clickX / rect.width;
      const newTime = percentage * this.duration;
      this.currentTime = newTime;
    }
  }

  @HostListener('document:mouseup')
  onTimelineDragEnd(): void {
    if (this.isDraggingTimeline) {
      this.isDraggingTimeline = false;
      this.seekTo(this.currentTime);
    }
  }

  // ========================================
  // Volume Controls
  // ========================================

  toggleMute(): void {
    const audio = this.audioPlayer?.nativeElement;
    if (audio) {
      audio.muted = !audio.muted;
      this.isMuted = audio.muted;
    }
  }

  setVolume(value: number): void {
    const audio = this.audioPlayer?.nativeElement;
    if (audio) {
      audio.volume = value;
      this.volume = value;
      this.isMuted = value === 0;
    }
  }

  // ========================================
  // Playback Speed
  // ========================================

  setPlaybackRate(rate: number): void {
    const audio = this.audioPlayer?.nativeElement;
    if (audio) {
      audio.playbackRate = rate;
      this.playbackRate = rate;
    }
  }

  // ========================================
  // Keyboard Shortcuts
  // ========================================

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    const audio = this.audioPlayer?.nativeElement;
    if (!audio) return;

    // Never hijack keys while typing (e.g. the comment box) — otherwise 'm'/'k'/space
    // trigger mute/play and get preventDefault'd so the character is never entered.
    const el = event.target as HTMLElement;
    const tag = el?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable) return;

    switch (event.key) {
      case ' ':
      case 'k':
        event.preventDefault();
        this.togglePlay();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.skip(-5);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.skip(5);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setVolume(Math.min(1, this.volume + 0.1));
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.setVolume(Math.max(0, this.volume - 0.1));
        break;
      case 'm':
        event.preventDefault();
        this.toggleMute();
        break;
    }
  }

  // ========================================
  // Utility Methods
  // ========================================

  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  getTimelineProgress(): number {
    return this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
  }

  getBufferedProgress(): number {
    return this.duration > 0 ? (this.buffered / this.duration) * 100 : 0;
  }

  getVolumeIcon(): string {
    if (this.isMuted || this.volume === 0) return 'fa-volume-mute';
    if (this.volume < 0.5) return 'fa-volume-down';
    return 'fa-volume-up';
  }

  // ========================================
  // Full screen (dialog resize, PDF-style)
  // ========================================
  private applyDialogSize(): void {
    if (this.isFullscreen) return;
    this.dialogRef.updateSize(this.showCommentsSidebar ? '1000px' : '680px', 'auto');
  }
  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen) {
      this.dialogRef.addPanelClass('audio-fullscreen');
      this.dialogRef.updateSize('100vw', '100vh');
    } else {
      this.dialogRef.removePanelClass('audio-fullscreen');
      this.applyDialogSize();
    }
  }

  // ========================================
  // Minimize to the floating mini-player
  // ========================================
  minimizeToMiniPlayer(): void {
    const audio = this.audioPlayer?.nativeElement;
    const currentTime = audio?.currentTime || 0;
    const isPlaying = !!audio && !audio.paused;
    if (audio) audio.pause();
    this.flushComments();
    this.mini.open(this.model, this.getMediaUrl(), this.model?.original_name || 'Audio File', currentTime, isPlaying);
    this.dialogRef.close();
  }

  // ========================================
  // Comments (timestamp-anchored)
  // ========================================
  /** Armed while the sidebar is open — clicking the player area drops a comment. */
  commentArmed = false;

  toggleCommentsSidebar(): void {
    this.showCommentsSidebar = !this.showCommentsSidebar;
    this.commentArmed = this.showCommentsSidebar;
    this.applyDialogSize();
  }

  /** Clicking anywhere on the player (while the sidebar is open) opens the add-comment
   *  popup at the current playback time — except on the transport controls, which keep
   *  their play/seek/volume behaviour. */
  onAudioAreaClick(event: MouseEvent): void {
    if (!this.commentArmed || this.newCommentTime !== null) return;
    const target = event.target as HTMLElement;
    if (target?.closest('.audio-controls')) return;   // don't hijack play/seek/volume
    this.startAddComment();
  }
  /** From the sidebar's "+ Add comment": anchor to the CURRENT playback position. */
  startAddComment(): void {
    const audio = this.audioPlayer?.nativeElement;
    this.newCommentTime = audio ? audio.currentTime : 0;
    this.newCommentText = '';
    this.showCommentsSidebar = true;
    this.applyDialogSize();
  }
  addComment(): void {
    if (this.newCommentTime === null || !this.newCommentText.trim()) return;
    const c: AudioComment = {
      id: `cmt-${Date.now()}-${audioCmtSeq++}`,
      x: 0, y: 0,
      mediaTime: this.newCommentTime,
      text: this.newCommentText.trim(),
      author: this.currentUserName,
      timestamp: new Date().toISOString(),
      status: 'open', priority: 'medium', replies: []
    };
    this.comments = [...this.comments, c];
    this.activeCommentId = c.id;
    this.newCommentTime = null;
    this.newCommentText = '';
    this.saveCommentsDebounced();
  }
  cancelComment(): void { this.newCommentTime = null; this.newCommentText = ''; }

  onCommentReply(e: { commentId: string; text: string }): void {
    this.comments = this.comments.map(c => c.id === e.commentId
      ? { ...c, replies: [...c.replies, { id: `r-${Date.now()}`, text: e.text, author: this.currentUserName, timestamp: new Date().toISOString() }] }
      : c);
    this.saveCommentsDebounced();
  }
  onCommentStatus(e: { id: string; status: CommentStatus }): void {
    this.comments = this.comments.map(c => c.id === e.id ? { ...c, status: e.status } : c);
    this.saveCommentsDebounced();
  }
  onCommentPriority(e: { id: string; priority: CommentPriority }): void {
    this.comments = this.comments.map(c => c.id === e.id ? { ...c, priority: e.priority } : c);
    this.saveCommentsDebounced();
  }
  deleteComment(id: string): void {
    this.comments = this.comments.filter(c => c.id !== id);
    if (this.activeCommentId === id) this.activeCommentId = null;
    this.saveCommentsDebounced();
  }
  /** Click a comment → seek the audio to its timestamp. */
  selectComment(id: string | null): void {
    this.activeCommentId = id;
    const c = id ? this.comments.find(x => x.id === id) : null;
    if (c && typeof c.mediaTime === 'number') this.seekTo(c.mediaTime);
  }

  private loadComments(): void {
    if (!this.documentId) { this.commentsLoaded = true; return; }
    this.markupService.getMarkup(this.documentId, this.isPreDefined).subscribe({
      next: r => { this.comments = (r?.comments as AudioComment[]) || []; this.commentsLoaded = true; },
      error: () => { this.commentsLoaded = true; }
    });
  }
  private saveCommentsDebounced(): void {
    this.commentsDirty = true;
    if (this.commentSaveTimer) clearTimeout(this.commentSaveTimer);
    this.commentSaveTimer = setTimeout(() => this.flushComments(), 600);
  }
  private flushComments(): void {
    if (this.commentSaveTimer) { clearTimeout(this.commentSaveTimer); this.commentSaveTimer = null; }
    if (!this.commentsDirty || !this.documentId || !this.commentsLoaded) return;
    this.commentsDirty = false;
    this.markupService.saveMarkup(this.documentId, [], this.comments, this.isPreDefined).subscribe({ error: () => {} });
  }
}
